import React from 'react';
import { Camera, CreditCard as Edit, MapPin, Heart, Star } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../components/Layout/BottomNavigation';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, isAuthenticated } = useSupabaseAuth();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const userProfile = {
    name: profile ? `${profile.first_name} ${profile.last_name}` : 'You',
    age: profile?.age || 25,
    location: profile?.location_city && profile?.location_country 
      ? `${profile.location_city}, ${profile.location_country}` 
      : 'Location not set',
    bio: profile?.bio || 'Love exploring new places, photography enthusiast, and coffee lover. Looking for someone to share adventures with!',
    interests: ['Photography', 'Travel', 'Coffee', 'Hiking', 'Music', 'Art'],
    photos: profile?.photos?.length ? profile.photos : [
      'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400',
      'https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?auto=compress&cs=tinysrgb&w=400',
      'https://images.pexels.com/photos/1542085/pexels-photo-1542085.jpeg?auto=compress&cs=tinysrgb&w=400'
    ]
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Profile</h1>
        <Button variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-purple-600">
          <Edit size={16} className="mr-1" />
          Edit
        </Button>
      </div>

      <div className="px-4 space-y-4">
        {/* Main Profile Card */}
        <Card className="p-6">
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <img
                src={userProfile.photos[0]}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-purple-200 dark:border-purple-600"
              />
              <button className="absolute bottom-0 right-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white hover:bg-purple-700 transition-colors">
                <Camera size={16} />
              </button>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-4">
              {userProfile.name}, {userProfile.age}
            </h2>
            
            <div className="flex items-center justify-center gap-1 text-gray-600 dark:text-gray-300 mt-2">
              <MapPin size={16} />
              <span>{userProfile.location}</span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Bio */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">About Me</h3>
              <p className="text-gray-600 dark:text-gray-300">{userProfile.bio}</p>
            </div>

            {/* Interests */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Interests</h3>
              <div className="flex flex-wrap gap-2">
                {userProfile.interests.map((interest, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-sm"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Photos Grid */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Photos</h3>
          <div className="grid grid-cols-3 gap-2">
            {userProfile.photos.map((photo, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={photo}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            ))}
            <button className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 hover:border-purple-400 hover:text-purple-400 transition-colors">
              <Camera size={24} />
            </button>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4 text-center">
            <Heart className="text-pink-500 mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">127</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Likes Received</p>
          </Card>
          
          <Card className="p-4 text-center">
            <Star className="text-yellow-500 mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">23</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Super Likes</p>
          </Card>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ProfilePage;